import { useEffect, useId, useRef, useState } from "react";
import { ZoomIn, ZoomOut, RotateCcw } from "lucide-react";
import { CORE_FEELINGS, UNKNOWN_COLOR, ink, tint, type CoreFeeling } from "@/data/feelings-pathway";

/**
 * Keeps every label inside its own segment, whatever language it is in.
 *
 * Labels are translated in place after the page renders, so a word can become
 * much longer (German, Dutch) or much shorter (Chinese). Each label carries the
 * widest space it may use; anything wider is stepped down in size until it fits.
 */
function useFitLabels(svgRef: React.RefObject<SVGSVGElement | null>) {
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;

    const fit = () => {
      const labels = svg.querySelectorAll<SVGTextElement>("text[data-fit-width]");
      labels.forEach((el) => {
        const max = Number(el.dataset["fitWidth"]);
        const base = Number(el.dataset["fitBase"]);
        if (!Number.isFinite(max) || !Number.isFinite(base)) return;
        let size = base;
        el.style.fontSize = `${size}px`;
        // A few passes: glyph widths do not scale perfectly linearly, so one
        // proportional step can still leave a word a hair too wide.
        for (let pass = 0; pass < 5; pass++) {
          let width = 0;
          try {
            width = el.getBBox().width;
          } catch {
            return;
          }
          if (width === 0 || width <= max) return;
          const next = Math.max(5.5, size * (max / width) * 0.98);
          if (next >= size) return;
          size = next;
          el.style.fontSize = `${size}px`;
        }
      });
    };

    let frame = requestAnimationFrame(fit);
    const observer = new MutationObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(fit);
    });
    observer.observe(svg, { subtree: true, characterData: true, childList: true });
    const onResize = () => fit();
    window.addEventListener("resize", onResize);
    // Labels measured before the display font arrives would be sized wrongly.
    void document.fonts?.ready.then(fit).catch(() => {});
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener("resize", onResize);
    };
  }, [svgRef]);
}

/**
 * Colourful two-ring emotion wheel (all viewports).
 *
 * Ring 1 = the twelve core feelings plus "I don't know what I'm feeling".
 * Ring 2 = the secondary feelings of whichever core feeling is selected,
 * fanned out around the segment it belongs to.
 *
 * Every segment is a real button-role element with a text label and a
 * screen-reader label, so colour is never the only signal. Labels live
 * inside the SVG so they scale with the wheel and always stay inside
 * their own coloured segment, at every screen width.
 *
 * The wheel is fixed inside its container: no panning, dragging or
 * zooming, so the page scrolls normally on touch devices.
 */

const SIZE = 440;
const C = SIZE / 2;
const R_CORE_IN = 88;
const R_CORE_OUT = 158;
const R_SEC_IN = 163;
const R_SEC_OUT = 214;

type Ring = { inner: number; outer: number };

function polar(r: number, deg: number) {
  const a = ((deg - 90) * Math.PI) / 180;
  // Rounded so the server and the browser produce byte-identical path data
  // (raw floats can differ in their last digit between JS engines).
  return {
    x: Number((C + r * Math.cos(a)).toFixed(3)),
    y: Number((C + r * Math.sin(a)).toFixed(3)),
  };
}

function arcPath(from: number, to: number, { inner, outer }: Ring) {
  const p1 = polar(outer, from);
  const p2 = polar(outer, to);
  const p3 = polar(inner, to);
  const p4 = polar(inner, from);
  const large = to - from > 180 ? 1 : 0;
  return [
    `M ${p1.x} ${p1.y}`,
    `A ${outer} ${outer} 0 ${large} 1 ${p2.x} ${p2.y}`,
    `L ${p3.x} ${p3.y}`,
    `A ${inner} ${inner} 0 ${large} 0 ${p4.x} ${p4.y}`,
    "Z",
  ].join(" ");
}

/** Long words are split with a hyphen so they still fit their segment. */
const LABEL_LINES: Record<string, readonly string[]> = {
  Shame: ["Shame"],
  Guilt: ["Guilt"],
  Sad: ["Sad"],
  Anxious: ["Anxious"],
  Angry: ["Angry"],
  Lonely: ["Lonely"],
  Rejected: ["Rejected"],
  Overwhelmed: ["Over-", "whelmed"],
  Numb: ["Numb"],
  Afraid: ["Afraid"],
  Disappointed: ["Disap-", "pointed"],
  Unworthy: ["Unworthy"],
  unknown: ["I don't", "know"],
};

/** Rough width of a bold glyph as a fraction of the font size. */
const GLYPH = 0.58;

/** Widest space available (tangentially) at a given radius for one slice. */
function chordAt(radius: number, sliceDeg: number) {
  return 2 * radius * Math.sin((sliceDeg * Math.PI) / 360);
}

/**
 * One font size for the whole first ring keeps the wheel visually balanced,
 * and it is derived from the tightest label so nothing can spill out.
 */
function fitCoreFontSize(sliceDeg: number, radius: number) {
  const available = chordAt(radius, sliceDeg) * 0.9;
  const longest = Object.values(LABEL_LINES)
    .flatMap((lines) => lines.map((l) => l.length))
    .reduce((a, b) => Math.max(a, b), 1);
  const byWidth = available / (longest * GLYPH);
  const byHeight = ((R_CORE_OUT - R_CORE_IN) * 0.72) / 2 / 1.05;
  return Math.max(8.5, Math.min(13.5, byWidth, byHeight));
}

const WATERCOLOUR = [
  "var(--emotion-shame)",
  "var(--emotion-guilt)",
  "var(--emotion-sad)",
  "var(--emotion-anxious)",
  "var(--emotion-angry)",
  "var(--emotion-lonely)",
  "var(--emotion-rejected)",
  "var(--emotion-overwhelmed)",
  "var(--emotion-numb)",
  "var(--emotion-afraid)",
  "var(--emotion-disappointed)",
  "var(--emotion-unworthy)",
  "var(--emotion-unknown)",
] as const;

export type EmotionWheelProps = {
  selected: CoreFeeling | null;
  onSelectCore: (f: CoreFeeling) => void;
  onSelectSecondary: (f: CoreFeeling, secondary: string) => void;
  onUnknown: () => void;
  compact?: boolean;
  /** Homepage preview stops at the first ring. */
  showSecondaryRing?: boolean;
  /** When true, show zoom in/out/reset controls on larger screens. */
  allowZoom?: boolean;
};

const ZOOM_KEY = "rt:emotion-wheel-zoom";
const MIN_ZOOM = 0.7;
const MAX_ZOOM = 1.8;
const ZOOM_STEP = 0.15;

function clampZoom(n: number) {
  return Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, Number(n.toFixed(2))));
}

function readSavedZoom(): number {
  if (typeof window === "undefined") return 1;
  try {
    const n = Number(JSON.parse(localStorage.getItem(ZOOM_KEY) ?? "1"));
    return Number.isFinite(n) ? clampZoom(n) : 1;
  } catch {
    return 1;
  }
}

export function EmotionWheel({
  selected,
  onSelectCore,
  onSelectSecondary,
  onUnknown,
  compact = false,
  showSecondaryRing = true,
  allowZoom = true,
}: EmotionWheelProps) {
  const [zoom, setZoom] = useState(readSavedZoom);
  const canZoom = allowZoom;
  const [focusedSlice, setFocusedSlice] = useState<string | null>(null);
  const gradId = useId().replace(/:/g, "");
  const svgRef = useRef<SVGSVGElement | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const [baseWidth, setBaseWidth] = useState(0);
  const slices = [...CORE_FEELINGS, null];
  const step = 360 / slices.length;
  const gap = 1.6;

  useFitLabels(svgRef);

  // A zero-height sizer carries the responsive default size, so the wheel can
  // grow in layout (not just visually) without feeding back into the measure.
  useEffect(() => {
    const el = stageRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const update = () => setBaseWidth(el.offsetWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const secondaryRing = selected && showSecondaryRing;
  // Crop empty outer space when only the inner ring is shown, so it fills the width.
  const VIEW = secondaryRing ? SIZE : 2 * (R_CORE_OUT + 12);
  const VB_OFF = (SIZE - VIEW) / 2;
  const selectedIndex = selected ? CORE_FEELINGS.findIndex((f) => f.key === selected.key) : -1;

  const labelRadius = (R_CORE_IN + R_CORE_OUT) / 2;
  const coreFont = fitCoreFontSize(step, labelRadius);
  const coreMaxWidth = chordAt(labelRadius, step) * 0.9;

  const applyZoom = (next: number) => {
    const clamped = clampZoom(next);
    setZoom(clamped);
    try {
      localStorage.setItem(ZOOM_KEY, JSON.stringify(clamped));
    } catch {}
  };

  // Pinch-to-zoom on touch/pen: two pointers only, so a single finger still
  // scrolls the page normally.
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const pinchStart = useRef<{ dist: number; zoom: number } | null>(null);

  const spread = () => {
    const pts = [...pointers.current.values()];
    if (pts.length < 2) return 0;
    const a = pts[0]!;
    const b = pts[1]!;
    return Math.hypot(a.x - b.x, a.y - b.y);
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (!canZoom || e.pointerType === "mouse") return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) pinchStart.current = { dist: spread(), zoom };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const start = pinchStart.current;
    if (!start || pointers.current.size < 2) return;
    const dist = spread();
    if (!dist || !start.dist) return;
    e.preventDefault();
    applyZoom(start.zoom * (dist / start.dist));
  };

  const endPointer = (e: React.PointerEvent) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinchStart.current = null;
  };

  // Keep the wheel centred inside its own horizontal scroll area when enlarged.
  // The viewport must NOT use justify-center: a centred flex item wider than
  // the container overflows equally on both sides, but the left overflow is
  // unreachable by scrolling, so the wheel would sit off-centre to the left.
  // Left-alignment plus explicit scroll centring keeps both edges reachable.
  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return;
    const frame = requestAnimationFrame(() => {
      vp.scrollLeft = Math.max(0, (vp.scrollWidth - vp.clientWidth) / 2);
    });
    return () => cancelAnimationFrame(frame);
  }, [zoom, baseWidth]);

  return (
    <div className="relative -mx-2 -mb-1 select-none sm:mx-0">
      {/* Screen-reader announcement of zoom changes. */}
      <span className="sr-only" role="status" aria-live="polite">
        {`Wheel size ${Math.round(zoom * 100)} percent`}
      </span>
      {canZoom && (
        <div
          className="absolute right-2 top-0 z-10 flex flex-col items-center gap-0.5 rounded-full bg-card/70 opacity-80 backdrop-blur-sm sm:right-0"
          role="group"
          aria-label="Wheel size controls. You can also press plus or minus while the wheel is focused."
        >
          <button
            type="button"
            onClick={() => applyZoom(zoom + ZOOM_STEP)}
            disabled={zoom >= MAX_ZOOM}
            aria-label={`Make the wheel bigger. Current size ${Math.round(zoom * 100)} percent`}
            aria-keyshortcuts="+"
            className="inline-flex h-11 w-11 items-center justify-center rounded-full text-foreground/75 transition hover:bg-gold/15 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-1 disabled:opacity-30"
          >
            <ZoomIn className="h-4 w-4" aria-hidden />
          </button>
          <button
            type="button"
            onClick={() => applyZoom(zoom - ZOOM_STEP)}
            disabled={zoom <= MIN_ZOOM}
            aria-label={`Make the wheel smaller. Current size ${Math.round(zoom * 100)} percent`}
            aria-keyshortcuts="-"
            className="inline-flex h-11 w-11 items-center justify-center rounded-full text-foreground/75 transition hover:bg-gold/15 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-1 disabled:opacity-30"
          >
            <ZoomOut className="h-4 w-4" aria-hidden />
          </button>
          {zoom !== 1 && (
            <button
              type="button"
              onClick={() => applyZoom(1)}
              aria-label="Reset wheel size to 100 percent"
              aria-keyshortcuts="0"
              className="inline-flex h-11 w-11 items-center justify-center rounded-full text-foreground/60 transition hover:bg-gold/15 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-1"
            >
              <RotateCcw className="h-3.5 w-3.5" aria-hidden />
            </button>
          )}
        </div>
      )}

      {/* Zero-height sizer: gives the default responsive wheel width. */}
      <div
        ref={stageRef}
        aria-hidden
        className={`pointer-events-none mx-auto h-0 w-full ${
          compact
            ? "max-w-[440px] md:max-w-[400px] lg:max-w-[560px]"
            : "max-w-[460px] md:max-w-[480px] lg:max-w-[640px]"
        }`}
      />

      <div
        ref={viewportRef}
        className="no-scrollbar flex touch-pan-y items-center overflow-x-auto overflow-y-hidden overscroll-x-contain"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endPointer}
        onPointerCancel={endPointer}
      >
        <div
          className={`relative mx-auto aspect-square w-full shrink-0 ${
            baseWidth
              ? ""
              : compact
                ? "max-w-[440px] md:max-w-[400px] lg:max-w-[560px]"
                : "max-w-[460px] md:max-w-[480px] lg:max-w-[640px]"
          }`}
          style={baseWidth ? { width: baseWidth * zoom, maxWidth: "none" } : undefined}
        >
        <div className="pointer-events-none absolute inset-[3%] rounded-full bg-gold/10 blur-2xl md:hidden" aria-hidden />
        <svg
          ref={svgRef}
          viewBox={`${VB_OFF} ${VB_OFF} ${VIEW} ${VIEW}`}
          className={`h-full w-full touch-pan-y overflow-visible drop-shadow-[0_16px_18px_color-mix(in_oklab,var(--gold-deep)_18%,transparent)] transition-transform duration-300 md:scale-100 ${
            compact || secondaryRing ? "scale-100" : "scale-[1.05]"
          }`}
          role="group"
          aria-label="Emotion wheel — choose the feeling closest to yours. Press plus to make the wheel bigger, minus to make it smaller, or zero to reset."
          aria-keyshortcuts="+ - 0"
          onKeyDown={(e) => {
            if (!canZoom) return;
            if (e.key === "+" || e.key === "=") {
              e.preventDefault();
              applyZoom(zoom + ZOOM_STEP);
            } else if (e.key === "-" || e.key === "_") {
              e.preventDefault();
              applyZoom(zoom - ZOOM_STEP);
            } else if (e.key === "0") {
              e.preventDefault();
              applyZoom(1);
            }
          }}
        >
          <defs>
            {slices.map((f, i) => {
              const color = WATERCOLOUR[i] ?? (f ? f.color : UNKNOWN_COLOR);
              return (
                <radialGradient key={i} id={`${gradId}-${i}`} cx="50%" cy="50%" r="70%">
                  {/* Colours come from CSS (.wheel-stop-*) so Night Mode can darken the slices. */}
                  <stop offset="0%" className="wheel-stop-a" style={{ "--emotion-c": color } as React.CSSProperties} />
                  <stop offset="65%" className="wheel-stop-b" style={{ "--emotion-c": color } as React.CSSProperties} />
                  <stop offset="100%" className="wheel-stop-c" style={{ "--emotion-c": color } as React.CSSProperties} />
                </radialGradient>
              );
            })}
            <linearGradient id={`${gradId}-gold-leaf`} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="var(--gold-light)" />
              <stop offset="45%" stopColor="var(--gold-deep)" />
              <stop offset="72%" stopColor="var(--gold)" />
              <stop offset="100%" stopColor="var(--gold-deep)" />
            </linearGradient>
            <filter id={`${gradId}-selected-shadow`} x="-30%" y="-30%" width="160%" height="160%">
              <feDropShadow dx="0" dy="5" stdDeviation="5" floodColor="var(--gold-deep)" floodOpacity="0.3" />
            </filter>
          </defs>

          <circle cx={C} cy={C} r={R_CORE_OUT + 4} fill="var(--card)" stroke={`url(#${gradId}-gold-leaf)`} strokeWidth="2" />

          {/* Ring 1 — core feelings. Each slice is its own tap/keyboard target. */}
          {slices.map((f, i) => {
            const from = i * step + gap / 2;
            const to = (i + 1) * step - gap / 2;
            const mid = (from + to) / 2;
            const color = WATERCOLOUR[i] ?? (f ? f.color : UNKNOWN_COLOR);
            const isSelected = f ? selected?.key === f.key : false;
            const dimmed = Boolean(selected) && !isSelected;
            // One whole word per label: a hyphen-split label would be
            // translated as two unrelated fragments. Long words are sized down
            // to fit instead (see useFitLabels).
            const label = f?.label ?? "I don't know";
            const { x, y } = polar(labelRadius, mid);
            const select = () => (f ? onSelectCore(f) : onUnknown());
            return (
              <g
                key={f?.key ?? "unknown"}
                role="button"
                tabIndex={0}
                aria-pressed={isSelected}
                aria-label={f ? `${f.label}. ${f.hint}` : "I don't know what I'm feeling"}
                data-wheel-label={f?.key ?? "unknown"}
                onClick={select}
                onFocus={() => setFocusedSlice(f?.key ?? "unknown")}
                onBlur={() => setFocusedSlice(null)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    select();
                  }
                }}
                style={{
                  cursor: "pointer",
                  opacity: dimmed ? 0.45 : 1,
                  transformOrigin: `${C}px ${C}px`,
                  transform: isSelected ? "scale(1.045)" : "scale(1)",
                  transition: "opacity .32s ease, transform .32s cubic-bezier(.2,.8,.2,1)",
                }}
              >
                <path
                  d={arcPath(from, to, { inner: R_CORE_IN, outer: R_CORE_OUT })}
                  fill={`url(#${gradId}-${i})`}
                  stroke={`url(#${gradId}-gold-leaf)`}
                  strokeOpacity={isSelected || focusedSlice === (f?.key ?? "unknown") ? 1 : 0.5}
                  strokeWidth={isSelected ? 3 : focusedSlice === (f?.key ?? "unknown") ? 2.5 : 1.15}
                  filter={isSelected ? `url(#${gradId}-selected-shadow)` : undefined}
                  style={{ transition: "stroke-opacity .32s ease, filter .32s ease" }}
                />
                <text
                  x={x}
                  y={y}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize={coreFont}
                  fontWeight={700}
                  fill={ink(color)}
                  data-fit-width={coreMaxWidth.toFixed(2)}
                  data-fit-base={coreFont.toFixed(2)}
                  style={{ pointerEvents: "none", fontFamily: "var(--font-emotion-label)" }}
                >
                  {label}
                </text>
              </g>
            );
          })}

          {/* Ring 2 — secondary feelings of the selected core feeling */}
          {secondaryRing &&
            selected.secondary.map((s, i) => {
              const centre = selectedIndex * step + step / 2;
              const span = Math.min(150, selected.secondary.length * 28);
              const w = span / selected.secondary.length;
              const from = centre - span / 2 + i * w + 0.8;
              const to = from + w - 1.6;
              const mid = (from + to) / 2;
              const r = (R_SEC_IN + R_SEC_OUT) / 2;
              const { x, y } = polar(r, mid);
              const maxWidth = chordAt(r, w) * 0.92;
              const size = Math.max(6.5, Math.min(10.5, maxWidth / (s.length * GLYPH)));
              return (
                <g
                  key={s}
                  role="button"
                  tabIndex={0}
                  aria-label={`${s} — a form of ${selected.label.toLowerCase()}`}
                  onClick={() => onSelectSecondary(selected, s)}
                  onFocus={() => setFocusedSlice(`sec-${s}`)}
                  onBlur={() => setFocusedSlice(null)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onSelectSecondary(selected, s);
                    }
                  }}
                  style={{ cursor: "pointer", animation: "fade-in .35s ease-out both", animationDelay: `${i * 45}ms` }}
                >
                  <path
                    d={arcPath(from, to, { inner: R_SEC_IN, outer: R_SEC_OUT })}
                    fill={tint(selected.color, 20 + i * 8)}
                    stroke={ink(selected.color)}
                    strokeOpacity={focusedSlice === `sec-${s}` ? 0.9 : 0.3}
                    strokeWidth={focusedSlice === `sec-${s}` ? 2 : 1}
                  />
                  <text
                    x={x}
                    y={y}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize={size}
                    fontWeight={600}
                    fill={ink(selected.color)}
                    data-fit-width={((R_SEC_OUT - R_SEC_IN) * 0.92).toFixed(2)}
                    data-fit-base={size.toFixed(2)}
                    style={{ pointerEvents: "none" }}
                    transform={`rotate(${mid > 180 ? mid + 90 : mid - 90} ${x} ${y})`}
                  >
                    {s}
                  </text>
                </g>
              );
            })}
        </svg>

        {/* Centre */}
        <div
          className={`pointer-events-none absolute inset-0 transition-transform duration-300 md:scale-100 ${
            secondaryRing ? "scale-100" : "scale-[1.05]"
          }`}
        >
          <div
            className="absolute start-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center rounded-full border border-gold/55 bg-card/95 text-center shadow-[0_8px_24px_-8px_color-mix(in_oklab,var(--gold-deep)_45%,transparent),inset_0_1px_0_color-mix(in_oklab,var(--gold-light)_70%,transparent)] ring-4 ring-card/55"
            style={{
              width: `${((R_CORE_IN * 2 - 12) / VIEW) * 100}%`,
              height: `${((R_CORE_IN * 2 - 12) / VIEW) * 100}%`,
              padding: "1rem",
            }}
          >
            {selected ? (
              <>
                <span
                  className={`font-display ${compact ? "text-sm" : "text-sm sm:text-base"}`}
                  style={{ color: ink(selected.color) }}
                >
                  {/* One sentence, so it is translated as a sentence. */}
                  {`You're feeling ${selected.label.toLowerCase()}`}
                </span>
                <span className="mt-1 hidden text-[10px] leading-relaxed text-muted-foreground sm:block sm:text-[11px]">
                  Choose a closer word around the edge, or keep going.
                </span>
              </>
            ) : (
              <>
                <span
                  className={`font-semibold text-foreground [font-family:var(--font-emotion-heading)] ${
                    compact ? "text-xs" : "text-xs sm:text-sm"
                  }`}
                >
                  How are you feeling right now?
                </span>
                <span className="mt-1 text-[8px] font-semibold uppercase text-[var(--gold-deep)] md:hidden">
                  Tap a colour
                </span>
                <span className="mt-1 hidden text-[10px] leading-relaxed text-muted-foreground sm:block sm:text-[11px]">
                  Choose what feels closest. We'll help you explore it.
                </span>
              </>
            )}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}

/** Small non-interactive wheel used as a dashboard/homepage teaser. */
export function MiniWheel({ size = 96 }: { size?: number }) {
  const step = 360 / (CORE_FEELINGS.length + 1);
  return (
    <svg width={size} height={size} viewBox={`0 0 ${SIZE} ${SIZE}`} aria-hidden className="flex-none">
      {[...CORE_FEELINGS, null].map((f, i) => (
        <path
          key={f?.key ?? "unknown"}
          d={arcPath(i * step + 1.6, (i + 1) * step - 1.6, { inner: 96, outer: 200 })}
          fill={tint(f ? f.color : UNKNOWN_COLOR, 55)}
        />
      ))}
      <circle cx={C} cy={C} r={88} fill="var(--cream, #FBF7EF)" />
    </svg>
  );
}
